import { useCallback, useEffect, useMemo } from 'react';
import { useInterval } from 'react-use';
import XButton from 'src/components/Buttons/XButton';
import TimedProgressBar from 'src/components/TimedProgresBar';
import AlertNotificationAction from 'src/components/Toasts/AlertNotifications/AlertNotificationAction';
import useNotificationPlacementSetting from 'src/hooks/useNotificationPlacementSetting';
import useNotificationStore from 'src/stores/useNotificationStore';
import { NotificationData } from 'src/utils/notifications';
import AlertToast from 'src/components/Toasts/AlertToast';
import { TOAST_BACKGROUND_CLASSNAME } from 'src/components/Toasts/consts';
import { cj } from 'src/utils/classNames';

const NOTIFICATION_DURATION_MILLIS = 15000;

interface Props {
	notification: NotificationData;
	footerHeight: number;
	onDismiss: (id: string) => void;
}

export default function MajorAlertNotification({
	notification,
	footerHeight,
	onDismiss,
}: Props) {
	const notificationStore = useNotificationStore();
	const {
		message,
		description,
		action,
		id,
		imageSrc,
		whiteXButton,
		disableAlertAutoDismiss,
	} = notification;
	const [notificationSelection] = useNotificationPlacementSetting();

	// Major announcements should always be positioned in the bottom right.
	// So, if the user has chosen a different position for their notifications,
	// we need to manually place the announcement back in the bottom right.
	const positionStyles = useMemo(() => {
		if (notificationSelection === 'bottomRight') {
			return;
		}

		return {
			position: 'fixed',
			bottom: footerHeight,
			right: 0,
		} as React.CSSProperties;
	}, [notificationSelection, footerHeight]);

	const dismissCurrentNotification = useCallback(() => {
		if (id) {
			notificationStore.removeNotificationById(id);
			onDismiss(id);
		}
	}, [notificationStore, id, onDismiss]);

	useInterval(
		dismissCurrentNotification,
		disableAlertAutoDismiss ? null : NOTIFICATION_DURATION_MILLIS
	);

	const hasImage = !!imageSrc;

	// An imageless major alert renders nothing, so with auto-dismiss off it
	// would sit in the store forever and block every later major alert. Drop it
	// without marking it seen, so it can still show if the config is fixed.
	useEffect(() => {
		if (!hasImage && id) {
			notificationStore.removeNotificationById(id);
		}
	}, [hasImage, id, notificationStore]);

	// If there's no image, we shouldn't be using a major alert and something has
	// been misconfigured on the BE.
	if (!imageSrc) {
		console.warn(
			'[MajorAlertNotifications] Major alert notification has no image. This should not happen.'
		);
		return null;
	}

	return (
		<AlertToast style={positionStyles}>
			<div
				className="relative flex min-h-[220px] w-full flex-col items-center justify-center"
				style={{
					backgroundImage: `url(${imageSrc})`,
					backgroundSize: 'cover',
					backgroundPosition: 'center',
				}}
			>
				<div className="absolute top-lg right-lg">
					<XButton
						onClick={dismissCurrentNotification}
						className={
							whiteXButton ? 'text-white hover:text-white/80' : undefined
						}
					/>
				</div>
			</div>
			<div
				className={cj(
					'flex flex-col gap-y-lg p-lg text-text-default',
					TOAST_BACKGROUND_CLASSNAME
				)}
			>
				<span className="typo-label-md">{message}</span>
				{description && <span className="typo-body-xs">{description}</span>}
				{action && 'label' in action && action.label && (
					<AlertNotificationAction
						alertType="major"
						action={action}
						dismissNotification={dismissCurrentNotification}
					>
						{action.label}
					</AlertNotificationAction>
				)}
			</div>
			{!disableAlertAutoDismiss && (
				<TimedProgressBar durationMillis={NOTIFICATION_DURATION_MILLIS} />
			)}
		</AlertToast>
	);
}
